import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarDays, FileStack, FolderInput, Paperclip } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import type { Party, WeeklyBatch } from '../api';
import { AppShell } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { Btn, SectionCard } from '../ui/components';
import { ActionQueueCard, MetricStrip } from '../ui/mobileOps';
import {
  Button,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextInput,
} from '../ui/primitives';

export function NewBatchPage() {
  const nav = useNavigate();
  const uplines = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });

  const [uplinePartyId, setUplinePartyId] = useState<string>('');
  const [weekMonday, setWeekMonday] = useState<string>(() => lastMondayIso());
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mondayReady = weekMonday !== '' && isMonday(weekMonday);
  const selectedUpline = useMemo(
    () => (uplines.data ?? []).find((upline) => String(upline.id) === uplinePartyId) ?? null,
    [uplines.data, uplinePartyId],
  );

  const createMut = useMutation({
    mutationFn: async (): Promise<{ batch: WeeklyBatch; created: boolean }> => {
      if (!uplinePartyId) throw new Error('Pick an upline.');
      if (!weekMonday) throw new Error('Pick a Monday date.');
      if (!isMonday(weekMonday)) throw new Error('Date must be a Monday.');
      const res = await api.post<{ batch: WeeklyBatch; created: boolean }>('/api/batches', {
        uplinePartyId: Number(uplinePartyId),
        weekMonday,
      });
      for (const file of files) {
        const base64 = await fileToBase64(file);
        await api.post<unknown>(`/api/batches/${res.batch.id}/attachments`, {
          base64,
          originalFilename: file.name,
          mimeType: file.type || 'application/octet-stream',
        });
      }
      return res;
    },
    onSuccess: ({ batch }) => {
      nav(`/batches/${batch.id}`);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <AppShell
      title="New batch"
      toolbar={
        <PageHeader
          section="batches"
          title="Create batch"
          subtitle="Open a new weekly import desk or jump back into an existing one for the same upline and Monday."
          breadcrumb={[{ label: 'Batches', to: '/batches' }]}
        />
      }
    >
      <div className="mx-auto max-w-5xl space-y-4">
        <MetricStrip
          section="batches"
          items={[
            { label: 'Uplines', value: uplines.isLoading ? '...' : String(uplines.data?.length ?? 0), note: 'available to import from' },
            { label: 'Week', value: mondayReady ? 'Ready' : 'Check', note: mondayReady ? 'valid Monday selected' : 'pick a Monday', tone: mondayReady ? 'pos' : 'warn' },
            { label: 'Files', value: String(files.length), note: files.length === 0 ? 'optional attachments' : 'will upload after batch create' },
          ]}
        />

        <div className="grid gap-4 lg:grid-cols-[1.08fr_0.92fr]">
          <SectionCard section="batches" title="Batch details">
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                createMut.mutate();
              }}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  label="Upline"
                  value={uplinePartyId}
                  onChange={setUplinePartyId}
                  options={(uplines.data ?? []).map((upline) => ({
                    value: String(upline.id),
                    label: upline.displayName,
                  }))}
                  placeholder="Choose..."
                  hint="Each batch is keyed by upline + Monday date."
                />
                <TextInput
                  label="Week start (Monday)"
                  type="date"
                  value={weekMonday}
                  onChange={(e) => setWeekMonday(e.target.value)}
                  hint="This must be the Monday for the import week."
                  error={weekMonday && !isMonday(weekMonday) ? 'That date is not a Monday.' : null}
                />
              </div>

              <div className="rounded-[24px] border border-dashed border-border/80 app-shell-surface-soft p-4 text-center backdrop-blur">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-[18px] bg-[var(--sec-batches-tint)] text-[var(--sec-batches)]">
                  <FolderInput className="h-5 w-5" strokeWidth={2} />
                </div>
                <div className="mt-3 text-small font-semibold text-ink">Attach the source files you want on the batch</div>
                <div className="mt-1 text-[12px] leading-5 text-ink-muted">
                  Screenshots, PDFs, and spreadsheets are fine. Files upload only after the batch record exists.
                </div>
                <div
                  className="mt-4 rounded-[20px] border border-border/70 bg-surface p-3 shadow-sm"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'copy';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const dropped = Array.from(e.dataTransfer.files);
                    setFiles((prev) => [...prev, ...dropped]);
                  }}
                >
                  <input
                    type="file"
                    multiple
                    accept=".xlsx,.jpg,.jpeg,.png,.pdf"
                    className="block w-full text-small text-ink-muted file:mr-3 file:cursor-pointer file:rounded-[14px] file:border file:border-border file:bg-white file:px-3 file:py-2 file:text-small file:font-medium file:text-ink"
                    onChange={(e) => {
                      const selectedFiles = e.target.files;
                      if (!selectedFiles) return;
                      setFiles((prev) => [...prev, ...Array.from(selectedFiles)]);
                    }}
                  />
                  <div className="mt-2 text-caption text-ink-muted">
                    Drop files here or pick them manually. Duplicate uploads are de-duped by SHA-256.
                  </div>
                </div>
              </div>

              {files.length > 0 && (
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between gap-3 rounded-[20px] border border-border/70 bg-surface px-4 py-3 shadow-sm backdrop-blur"
                    >
                      <div className="min-w-0 flex items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] bg-[var(--sec-batches-tint)] text-[var(--sec-batches)]">
                          <Paperclip className="h-4 w-4" strokeWidth={2} />
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-small font-medium text-ink">{file.name}</div>
                          <div className="mt-0.5 text-caption text-ink-muted">{(file.size / 1024).toFixed(0)} kB</div>
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index))}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {error && <ErrorBanner>{error}</ErrorBanner>}

              <div className="flex flex-wrap items-center gap-3">
                <Btn type="submit" loading={createMut.isPending}>
                  Create or open batch
                </Btn>
                <span className="text-caption text-ink-muted">
                  If the same upline-week batch already exists, you will jump back into it instead of creating a duplicate.
                </span>
              </div>
            </form>
          </SectionCard>

          <div className="space-y-4">
            <ActionQueueCard
              section="batches"
              eyebrow="Before you create"
              title={selectedUpline ? `${selectedUpline.displayName} · ${weekMonday}` : 'Pick the weekly key first'}
              description={
                mondayReady
                  ? 'Once the batch opens, you can import rows, review exceptions, verify, and post from the same desk.'
                  : 'The week date drives reporting and must be a Monday so balances stay aligned with the batch cycle.'
              }
              tone={mondayReady ? 'accent' : 'warn'}
              meta={
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span>Selected upline</span>
                    <span className="font-medium text-ink">{selectedUpline?.displayName ?? 'Not chosen yet'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>Week check</span>
                    <span className="font-medium text-ink">{mondayReady ? 'Monday confirmed' : 'Needs correction'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>Attachments</span>
                    <span className="font-medium text-ink">{files.length === 0 ? 'Optional' : `${files.length} queued`}</span>
                  </div>
                </div>
              }
            />

            <SectionCard section="batches" title="What belongs here">
              <div className="space-y-3 text-[13px] leading-5 text-ink-muted">
                <div className="flex gap-3 rounded-[20px] bg-surface-raised p-3 ring-1 ring-border/50">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] bg-[var(--sec-batches-tint)] text-[var(--sec-batches)]">
                    <CalendarDays className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <div>
                    Use the Monday that represents the settlement week. This keeps the batch timeline and ledger posting window consistent.
                  </div>
                </div>
                <div className="flex gap-3 rounded-[20px] bg-surface-raised p-3 ring-1 ring-border/50">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] bg-[var(--sec-batches-tint)] text-[var(--sec-batches)]">
                    <FileStack className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <div>
                    Good attachments include the original upline sheet, screenshots of disputed rows, and any exported workbook you may need later for audit.
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('unexpected FileReader result'));
        return;
      }
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

function isMonday(isoDate: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return false;
  const date = new Date(`${isoDate}T00:00:00Z`);
  return date.getUTCDay() === 1;
}

function lastMondayIso(): string {
  const today = new Date();
  const day = today.getUTCDay();
  const deltaDays = day === 0 ? -6 : 1 - day;
  const date = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + deltaDays));
  const year = date.getUTCFullYear().toString().padStart(4, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const dayOfMonth = date.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${dayOfMonth}`;
}
